import { TViewDefinition } from '../utils/view-resolver-utils';
import { useCallback } from 'react';

interface ICacheData {
  data: TViewDefinition;
  timestamp: number;
}

const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes in milliseconds

export const generateCacheKey = (locale: string): string => {
  return `search-content-${locale}`;
};

export const getCachedData = (cacheKey: string): TViewDefinition | null => {
  try {
    const cachedItem = sessionStorage.getItem(cacheKey);
    if (!cachedItem) return null;

    const cached: ICacheData = JSON.parse(cachedItem);
    const now = Date.now();

    // Check if cache is still valid (within 10 minutes)
    if (now - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }

    // Cache expired, remove it
    sessionStorage.removeItem(cacheKey);
    return null;
  } catch {
    // Cache read failed, return null to trigger fresh fetch
    return null;
  }
};

export const setCachedData = (cacheKey: string, data: TViewDefinition): void => {
  try {
    const cacheData: ICacheData = {
      data,
      timestamp: Date.now(),
    };
    sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
  } catch {
    // Cache write failed - non-critical, continue without caching
  }
};

/**
 * Function to verify the link URL is external or internal
 * @param linkURL the URL to be verified
 * @returns Boolean value true if it is external false otherwise
 */
export const verifyLinkType = (linkUrl: string): boolean => {
  const pattern = /^(https?:\/\/)/;
  const hasValidUrlProtocol = pattern.test(linkUrl);

  return hasValidUrlProtocol;
};

/**
 * Function to redirect the link or route based on external or internal
 * @param routerOrContext react router, next router, or SDMRouterContext object is passed down as parameter
 * @returns callback function
 */
//eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useRedirectLink = (routerOrContext: any) => {
  // Handle both { router } context object (from useSDMRouterContext()) and direct router
  const router = routerOrContext?.router ?? routerOrContext;
  return useCallback(
    (redirectUrl?: string) => {
      if (typeof redirectUrl !== 'string') return;
      const isExternal = verifyLinkType(redirectUrl || '');
      // Only push to router if the route is not empty
      if (redirectUrl) {
        if (isExternal) window.location.href = redirectUrl;
        else router.push(redirectUrl);
      }
    },
    [router]
  );
};

export const extractPathFromBaseUrl = (url?: string): string => {
  if (!url) return '';

  try {
    // If the URL doesn't have a protocol, default to https
    const parsedUrl = new URL(url, 'https://loblaws.ca');
    return parsedUrl.pathname;
  } catch {
    // Invalid URL format, return empty string
    return '';
  }
};
